import { ChevronRight, Plus, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { PaneHeader } from '@/components/settings/pane-header';
import { permissionsSummary } from '@/components/settings/team/permission-matrix';

/** Settings → Team → Access groups: the group list; clicking a row opens the editor pane. */
export function GroupsListPane({ groups, loading, creating, onCreate, onEdit }) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <PaneHeader
        title="Access groups"
        description="Named permission bundles assigned to employees; rights add up across a person's groups."
        action={
          <Button type="button" size="sm" onClick={onCreate} disabled={creating}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden />
            New group
          </Button>
        }
      />
      <ScrollArea className="min-h-0 flex-1">
        {loading ? (
          <div className="space-y-2 p-5">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">
            No access groups yet. Create one, set its function matrix, then assign it to employees.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {groups.map((group) => (
              <li key={group.id}>
                <button
                  type="button"
                  onClick={() => onEdit(group.id)}
                  className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-500/15 text-violet-700 ring-1 ring-violet-400/30 dark:text-violet-300">
                    <ShieldCheck className="h-4.5 w-4.5" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-foreground">
                      {group.name || 'Untitled group'}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {group.description || permissionsSummary(group.permissions)}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {permissionsSummary(group.permissions)}
                    {/* "items" not "menu items": the list now spans the sidebar AND the
                        Settings rail. */}
                    {group.hiddenMenuItems?.length
                      ? ` · ${group.hiddenMenuItems.length} item${group.hiddenMenuItems.length === 1 ? '' : 's'} hidden`
                      : ''}
                    {group.hiddenClinics?.length
                      ? ` · ${group.hiddenClinics.length} clinic tab${group.hiddenClinics.length === 1 ? '' : 's'} hidden`
                      : ''}
                    {group.chatDirectoryVisible ? ' · visible in chat' : ''}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
      </ScrollArea>
    </div>
  );
}
